import React, { useRef } from 'react'
import {
  Box,
  Chip,
  MenuItem,
  Select as MuiSelect,
  SelectProps as MuiSelectProps,
  Stack,
  Typography,
} from '@mui/material'

type Option = { label: string; value: string } & Record<string, any>

type SingleSelectProps = {
  placeholder?: string
  value: string
  onChange: (value: string, option: Option, el: HTMLElement) => void
  onClick?: (option: Option, el: HTMLElement) => void
  slotProps?: { root?: MuiSelectProps }
  maxHeight?: number
  options: Option[]
  multiple?: false
  showChip?: never
  maxChips?: never
}

type MultipleSelectProps = {
  placeholder?: string
  value: string[]
  onChange: (value: string[], options: Option[], el: HTMLElement) => void
  onClick?: (option: Option, el: HTMLElement) => void
  slotProps?: { root?: MuiSelectProps }
  maxHeight?: number
  options: Option[]
  multiple: true
  showChip?: boolean
  maxChips?: number
}

const ellipsisStyle = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

function Select(props: SingleSelectProps): JSX.Element
function Select(props: MultipleSelectProps): JSX.Element
function Select(props: SingleSelectProps | MultipleSelectProps): JSX.Element {
  const {
    placeholder,
    options: optionsProp,
    slotProps,
    maxHeight = 300,
    multiple,
    showChip,
    maxChips = 3,
    value,
    onChange,
    onClick,
  } = props
  const selectRef = useRef(null)

  const options = Array.isArray(optionsProp) ? optionsProp : []

  const menuProps = {
    PaperProps: {
      anchorEl: selectRef.current,
      sx: {
        maxHeight: maxHeight,
        width: selectRef.current
          ? selectRef.current?.node.clientWidth + 'px'
          : undefined,
        '::-webkit-scrollbar': {
          width: '8px',
        },
        '::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '4px',
        },
        '::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: '4px',
        },
        '::-webkit-scrollbar-thumb:hover': {
          background: '#a8a8a8',
        },
      },
    },
  }
  if (multiple)
    return (
      <MuiSelect
        displayEmpty
        size="small"
        value={value}
        multiple
        onChange={event => {
          const {
            target: { value },
          } = event

          const values =
            typeof value === 'string' ? value.split(',') : (value as string[])
          const selectedOptions = options.filter(option =>
            values.includes(option.value),
          )

          onChange(values, selectedOptions, selectRef.current?.node)
        }}
        renderValue={selected => {
          if ((selected as string[])?.length === 0) {
            return (
              <Typography
                variant="body3"
                sx={{
                  // fontWeight: 400,
                  ...ellipsisStyle,
                }}
                // component="em"
              >
                {placeholder}
              </Typography>
            )
          }

          const selectedValues = options
            .filter(option => (selected as string[]).includes(option.value))
            .map(option => option.label)

          if (showChip) {
            const chipsToShow = selectedValues.slice(0, maxChips!)
            const remainingCount = selectedValues.length - maxChips!
            return (
              <Stack direction="row" alignItems="center" gap={1}>
                {chipsToShow.map(item => {
                  const option = options.find(opt => opt.label === item)
                  return (
                    <Box
                      key={item}
                      onMouseDown={e => e.stopPropagation()} // Prevent focus on Select
                    >
                      <Chip
                        label={item}
                        size="small"
                        onDelete={() => {
                          const newValues = (selected as string[]).filter(
                            value => value !== option?.value,
                          )
                          onChange(
                            newValues,
                            options.filter(option =>
                              newValues.includes(option.value),
                            ),
                            selectRef.current?.node,
                          )
                        }}
                      />
                    </Box>
                  )
                })}
                {remainingCount > 0 && (
                  <Typography
                    variant="caption2"
                    sx={{ color: 'text.secondary' }}
                  >
                    +{remainingCount} more
                  </Typography>
                )}
              </Stack>
            )
          }

          return (
            <Typography variant="body3" sx={ellipsisStyle}>
              {selectedValues.join(', ')}
            </Typography>
          )
        }}
        fullWidth
        MenuProps={menuProps}
        inputRef={selectRef}
        {...slotProps?.root}
      >
        {options.map(option => (
          <MenuItem
            key={option.value}
            value={option.value}
            onClick={() => onClick?.(option, selectRef.current?.node)}
          >
            <Typography variant="body3" sx={ellipsisStyle}>
              {option.label}
            </Typography>
          </MenuItem>
        ))}
      </MuiSelect>
    )

  if (!multiple)
    return (
      <MuiSelect
        displayEmpty
        size="small"
        value={value}
        onChange={event => {
          const {
            target: { value },
          } = event
          const selectedOption = options.find(option => option.value === value)

          onChange(
            value as any,
            selectedOption! as any,
            selectRef.current?.node,
          )
        }}
        renderValue={selected => {
          if (!selected) {
            return (
              <Typography
                variant="body3"
                sx={{
                  // fontWeight: 400,
                  ...ellipsisStyle,
                }}
                // component="em"
              >
                {placeholder}
              </Typography>
            )
          }

          const selectedOption = options.find(opt => opt.value === selected)
          return (
            <Typography variant="body3" sx={ellipsisStyle}>
              {selectedOption?.label}
            </Typography>
          )
        }}
        fullWidth
        MenuProps={menuProps}
        inputRef={selectRef}
        {...slotProps?.root}
      >
        {options.map(option => (
          <MenuItem
            key={option.value}
            value={option.value}
            onClick={() => onClick?.(option, selectRef.current?.node)}
          >
            <Typography variant="body3" sx={ellipsisStyle}>
              {option.label}
            </Typography>
          </MenuItem>
        ))}
      </MuiSelect>
    )
}

export default Select
